import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

interface InlineTextEditProps {
  value: string;
  onCommit: (next: string) => void;
  className?: string;
  placeholder?: string;
  /** Rendered with a strikethrough, for completed tasks. */
  muted?: boolean;
  disabled?: boolean;
}

/**
 * Click-to-edit text. Enter or blur commits, Escape reverts.
 *
 * `stopPropagation` on pointer-down matters here: this lives inside a draggable
 * card, and without it dnd-kit's sensor swallows the click and the field can
 * never be focused.
 */
export function InlineTextEdit({
  value,
  onCommit,
  className,
  placeholder,
  muted,
  disabled,
}: InlineTextEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Adopt server-side changes (a rejected edit rolling back, another tab) while
  // never clobbering what the user is currently typing.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  function commit() {
    const next = draft.trim();
    setEditing(false);
    if (!next) {
      // An empty title is not a save, it is a mistake — put the old one back.
      setDraft(value);
      return;
    }
    if (next !== value) onCommit(next);
  }

  if (disabled || !editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => !disabled && setEditing(true)}
        className={clsx(
          'w-full text-left',
          !disabled && 'hover:underline decoration-border-dark underline-offset-2',
          muted && 'line-through text-text-tertiary',
          className,
        )}
      >
        {value || <span className="text-text-muted">{placeholder ?? 'Untitled'}</span>}
      </button>
    );
  }

  return (
    <input
      ref={inputRef}
      value={draft}
      autoFocus
      onPointerDown={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setDraft(value);
          setEditing(false);
        }
      }}
      className={clsx(
        'w-full rounded border border-border bg-surface-primary px-1.5 py-0.5 outline-none focus:border-text-primary',
        className,
      )}
    />
  );
}
